import React, { useRef, useState, useEffect } from 'react';

export const CustomVideoPlayer = ({ url, title, isThumbnail = false }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    let timeout;
    if (isPlaying) {
      timeout = setTimeout(() => setShowControls(false), 3000);
    } else {
      setShowControls(true);
    }
    return () => clearTimeout(timeout);
  }, [isPlaying, showControls]);

  const handlePlayPause = (e) => {
    if (e) e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current.duration);
  };

  const handleProgressClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = pos * duration;
    }
  };

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    if (!document.fullscreenElement) {
      playerRef.current?.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const formatTime = (time) => {
    if (isNaN(time)) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleSpeedChange = (e) => {
    e.stopPropagation();
    const rates = [1, 1.25, 1.5, 2];
    const nextRateIndex = (rates.indexOf(playbackRate) + 1) % rates.length;
    const nextRate = rates[nextRateIndex];
    setPlaybackRate(nextRate);
    if (videoRef.current) videoRef.current.playbackRate = nextRate;
  };

  return (
    <div 
      ref={playerRef} 
      className="custom-video-player position-absolute top-0 start-0 w-100 h-100 overflow-hidden rounded-3 bg-dark"
      onMouseMove={() => setShowControls(true)}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onClick={() => setShowControls(true)}
    >
      <video
        ref={videoRef}
        src={url}
        className="w-100 h-100 object-fit-cover"
        onClick={handlePlayPause}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Overlay - Top Left Badge (Hide in thumbnail) */}
      {!isThumbnail && (
        <div className={`position-absolute top-0 start-0 p-3 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`} style={{ transition: 'opacity 0.3s' }}>
          <div className="text-white px-3 py-1 rounded-3 d-inline-block" style={{ fontSize: '0.75rem', backgroundColor: 'rgba(0,0,0,0.4)' }}>
            {title || 'Lesson 1 of 1'}
          </div>
        </div>
      )}
      
      {/* Play Button Overlay (when paused) */}
      {!isPlaying && (
        <div className="position-absolute top-50 start-50 translate-middle" onClick={handlePlayPause} style={{ cursor: 'pointer' }}>
          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: isThumbnail ? '48px' : '64px', height: isThumbnail ? '48px' : '64px', backgroundColor: 'rgba(0,0,0,0.4)' }}>
            <i className="bi bi-play-fill text-white ms-1" style={{ fontSize: isThumbnail ? '2rem' : '2.5rem' }} />
          </div>
        </div>
      )}

      {/* Controls Overlay (Hide entirely in thumbnail view) */}
      {!isThumbnail && (
        <div 
          className={`video-controls position-absolute bottom-0 start-0 w-100 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))', transition: 'opacity 0.3s' }}
        >
          {/* Progress Bar */}
          <div 
            className="progress-bar-container w-100 position-relative px-3 mb-1" 
            style={{ height: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={handleProgressClick}
          >
            <div className="w-100 position-relative" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '2px' }}>
              <div 
                className="progress-fill h-100 rounded-start"
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%`, transition: 'width 0.1s linear', backgroundColor: '#7B61FF' }} 
              />
              <div 
                className="progress-thumb position-absolute top-50 translate-middle rounded-circle"
                style={{ 
                  width: '12px', height: '12px', backgroundColor: '#7B61FF', 
                  left: `${duration ? (currentTime / duration) * 100 : 0}%`,
                  display: showControls ? 'block' : 'none'
                }} 
              />
            </div>
          </div>

          {/* Controls Bar */}
          <div className="d-flex align-items-center justify-content-between px-3 pb-3 pt-1 text-white">
            <div className="d-flex align-items-center gap-3">
              <button onClick={handlePlayPause} className="btn btn-link text-white p-0 m-0 border-0 d-flex align-items-center justify-content-center" style={{ width: '24px' }}>
                <i className={`bi ${isPlaying ? 'bi-pause' : 'bi-play'}`} style={{ fontSize: '1.25rem' }} />
              </button>
              <button className="btn btn-link text-white p-0 m-0 border-0 d-flex align-items-center justify-content-center" style={{ width: '24px' }}>
                <i className="bi bi-skip-end" style={{ fontSize: '1.25rem' }} />
              </button>
              
              <div className="d-flex align-items-center gap-2 volume-control">
                <button className="btn btn-link text-white p-0 m-0 border-0 d-flex align-items-center justify-content-center" style={{ width: '24px' }}>
                  <i className={`bi ${volume === 0 ? 'bi-volume-mute' : volume < 0.5 ? 'bi-volume-down' : 'bi-volume-up'}`} style={{ fontSize: '1.25rem' }} />
                </button>
              </div>

              <div className="time-display fw-normal ms-1" style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <button 
                onClick={handleSpeedChange} 
                className="btn btn-outline-light d-flex align-items-center justify-content-center p-0 fw-medium"
                style={{ width: '32px', height: '24px', fontSize: '0.75rem', borderColor: 'rgba(255,255,255,0.4)', borderRadius: '4px', opacity: 0.9 }}
              >
                {playbackRate}x
              </button>
              <button 
                className="btn btn-outline-light d-flex align-items-center justify-content-center p-0 fw-medium"
                style={{ width: '32px', height: '24px', fontSize: '0.75rem', borderColor: 'rgba(255,255,255,0.4)', borderRadius: '4px', opacity: 0.9 }}
              >
                CC
              </button>
              <button className="btn btn-link text-white p-0 m-0 border-0 d-flex align-items-center justify-content-center" style={{ width: '24px', opacity: 0.9 }}>
                <i className="bi bi-gear" style={{ fontSize: '1.1rem' }} />
              </button>
              <button onClick={toggleFullscreen} className="btn btn-link text-white p-0 m-0 border-0 d-flex align-items-center justify-content-center" style={{ width: '24px', opacity: 0.9 }}>
                <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`} style={{ fontSize: '1.1rem' }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
